/* eslint-disable max-classes-per-file -- encoder e decoder são o par do mesmo codec */
/* global VideoEncoder, VideoDecoder, EncodedVideoChunk, MediaStreamTrackProcessor, MediaStreamTrackGenerator */
// Pipeline de vídeo do AstraCall — H264 sobre DataChannel via WebCodecs.
// Port fiel da referência do AstraCalls (client/src/lib/call/video-codec.ts).
// NÃO alterar os parâmetros: é o que a ponte com o WhatsApp comporta com fluidez.
export const H264_CHANNEL_LABEL = 'h264';
export const VIDEO_CODEC = 'avc1.42E01F'; // H264 baseline, Annex B
export const VIDEO_WIDTH = 160;
export const VIDEO_HEIGHT = 120;
export const VIDEO_FPS = 15;
export const VIDEO_BITRATE = 50_000;
export const VIDEO_KEYFRAME_INTERVAL = VIDEO_FPS; // ~1s

// Gate de navegador: vídeo depende de WebCodecs + Insertable Streams.
// Chrome/Edge/Chromium ✅ · Safari/iOS/Firefox ❌ (cai pra áudio-only).
export const videoSupported = () =>
  typeof window !== 'undefined' &&
  'VideoEncoder' in window &&
  'VideoDecoder' in window &&
  'MediaStreamTrackProcessor' in window &&
  'MediaStreamTrackGenerator' in window;

// keyframe em Annex B = NAL type 5 (IDR) ou 7 (SPS)
const isAnnexBKeyframe = b => {
  for (let i = 0; i + 4 < b.length; i += 1) {
    const tresBytes = b[i] === 0 && b[i + 1] === 0 && b[i + 2] === 1;
    const quatroBytes =
      b[i] === 0 && b[i + 1] === 0 && b[i + 2] === 0 && b[i + 3] === 1;
    const sc = (tresBytes && 3) || (quatroBytes && 4) || 0;
    if (sc) {
      // eslint-disable-next-line no-bitwise -- tipo do NAL são os 5 bits baixos
      const t = b[i + sc] & 0x1f;
      if (t === 5 || t === 7) return true;
      i += sc;
    }
  }
  return false;
};

// Encoder: lê frames da câmera (MediaStreamTrackProcessor) → H264 → callback send.
export class VideoSender {
  constructor(track, send) {
    this.frameCount = 0;
    this.closed = false;
    this.encoder = new VideoEncoder({
      output: chunk => {
        const buf = new Uint8Array(chunk.byteLength);
        chunk.copyTo(buf);
        send(buf.buffer);
      },
      error: e => {
        // eslint-disable-next-line no-console
        console.error('video encoder error', e);
      },
    });
    this.encoder.configure({
      codec: VIDEO_CODEC,
      width: VIDEO_WIDTH,
      height: VIDEO_HEIGHT,
      bitrate: VIDEO_BITRATE,
      framerate: VIDEO_FPS,
      latencyMode: 'realtime',
      avc: { format: 'annexb' },
    });
    const processor = new MediaStreamTrackProcessor({ track });
    this.reader = processor.readable.getReader();
    this.pump();
  }

  async pump() {
    while (!this.closed) {
      // eslint-disable-next-line no-await-in-loop
      const { value: frame, done } = await this.reader.read();
      if (done || !frame) break;
      if (this.encoder.encodeQueueSize < 2) {
        const keyFrame = this.frameCount % VIDEO_KEYFRAME_INTERVAL === 0;
        this.encoder.encode(frame, { keyFrame });
        this.frameCount += 1;
      }
      frame.close();
    }
  }

  close() {
    this.closed = true;
    try {
      this.reader.cancel();
    } catch (e) {
      /* noop */
    }
    try {
      this.encoder.close();
    } catch (e) {
      /* noop */
    }
  }
}

// Decoder: recebe unidades de acesso H264 → VideoFrame → MediaStream (via generator).
export class VideoReceiver {
  constructor() {
    this.ts = 0;
    this.started = false;
    this.writing = false;
    const generator = new MediaStreamTrackGenerator({ kind: 'video' });
    this.writer = generator.writable.getWriter();
    this.stream = new MediaStream([generator]);
    this.decoder = new VideoDecoder({
      output: frame => {
        if (this.writing) {
          frame.close();
          return;
        }
        this.writing = true;
        this.writer
          .write(frame)
          .catch(() => frame.close())
          .finally(() => {
            this.writing = false;
          });
      },
      error: e => {
        // eslint-disable-next-line no-console
        console.error('video decoder error', e);
      },
    });
    this.decoder.configure({ codec: VIDEO_CODEC, optimizeForLatency: true });
  }

  decode(data) {
    const bytes = new Uint8Array(data);
    const key = isAnnexBKeyframe(bytes);
    // só começa a decodificar a partir de um keyframe
    if (!this.started && !key) return;
    this.started = true;
    const chunk = new EncodedVideoChunk({
      type: key ? 'key' : 'delta',
      timestamp: this.ts,
      data: bytes,
    });
    this.ts += 1_000_000 / VIDEO_FPS;
    try {
      this.decoder.decode(chunk);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('video decode error', e);
    }
  }

  close() {
    try {
      this.decoder.close();
    } catch (e) {
      /* noop */
    }
    try {
      this.writer.close();
    } catch (e) {
      /* noop */
    }
  }
}
